import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, DataTable, EmptyState, FilterBar, FilterChips, Pagination, Pill, fmtBRL, type Column } from "@atlas/ui/web";
import type { EstadoRefinanciamento, Refinanciamento } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { aplicarFiltros, contagens, motivoVazio, POR_PAGINA_PADRAO, recortar } from "../../lib/recorte";
import { matchAnyKeys } from "../../lib/text-search";
import { useFiltros } from "../../lib/use-filtros";
import { mensagemDeErro } from "../../lib/erro-validacao";
import { ESTADO_REFIN_LABEL_OPERADOR, acoesDoRefin, fmtDataHora, fmtTaxaFracao, pillDoEstadoRefin } from "../../lib/refinanciamento";
import {
  CancelarRefinanciamentoModal,
  ResponderRefinanciamentoModal,
} from "../../components/RefinanciamentoBancoModais";

const ESTADOS_FILA: EstadoRefinanciamento[] = ["solicitado", "ofertado", "aceito", "efetivado", "recusado", "cancelado", "expirado"];

type Linha = Refinanciamento & { adfs: string };

/** Fila de refinanciamentos do banco: pedidos a responder e ofertas em curso. */
export function BancoRefinanciamentos() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["banco", "refinanciamentos"],
    queryFn: () => atlas.refinanciamento.doBanco(),
    refetchInterval: 10_000,
    placeholderData: (prev) => prev,
  });
  const [estado, setEstado] = useState<"" | EstadoRefinanciamento>("");
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  const [responder, setResponder] = useState<Refinanciamento | null>(null);
  const [cancelar, setCancelar] = useState<Refinanciamento | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const base: Linha[] = useMemo(
    () => (q.data?.itens ?? []).map((r) => ({ ...r, adfs: r.origens.map((o) => o.adf).join(" ") })),
    [q.data],
  );
  const termo = busca.trim();
  const filtros = useMemo(() => ({
    estado: estado ? (r: Linha) => r.estado === estado : null,
    busca: termo ? (r: Linha) => matchAnyKeys(r, termo, ["id", "servidorNome", "matricula", "cpfMasked", "adfs"]) : null,
  }), [estado, termo]);
  const linhas = useMemo(() => aplicarFiltros(base, filtros), [base, filtros]);
  const semEstado = useMemo(() => aplicarFiltros(base, filtros, "estado"), [base, filtros]);
  const contEstado = useMemo(() => contagens(semEstado, (r) => r.estado), [semEstado]);

  const filtrosUi = useFiltros(
    [
      { nome: "busca", ligado: busca !== "", limpar: () => setBusca("") },
      { nome: "estado", ligado: estado !== "", limpar: () => setEstado("") },
    ],
    setPagina,
  );
  const { filtrar } = filtrosUi;
  const daPagina = recortar(linhas, pagina, porPagina);

  const feito = (r: Refinanciamento, texto: string) => {
    setResponder(null);
    setCancelar(null);
    setAviso(texto.replace("{id}", r.id));
    void qc.invalidateQueries({ queryKey: ["banco", "refinanciamentos"] });
    void qc.invalidateQueries({ queryKey: ["banco", "propostas-api"] });
    void qc.invalidateQueries({ queryKey: ["banco", "contratos-api"] });
  };

  const columns: Column<Linha>[] = [
    { key: "estado", header: "Estado", render: (r) => <Pill variant={pillDoEstadoRefin(r.estado)}>{ESTADO_REFIN_LABEL_OPERADOR[r.estado]}</Pill> },
    { key: "id", header: "Refinanciamento", mono: true, render: (r) => r.id },
    {
      key: "servidor",
      header: "Servidor",
      render: (r) => (
        <>
          <div>{r.servidorNome}</div>
          <div style={{ fontSize: 11, color: "var(--text-dim)" }}>{r.cpfMasked} / {r.matricula}</div>
        </>
      ),
    },
    { key: "origens", header: "Contratos de origem", mono: true, render: (r) => r.origens.map((o) => o.adf).join(", ") },
    { key: "parcelasOrigem", header: "Parcelas atuais", align: "right", render: (r) => fmtBRL(r.parcelasOrigem) },
    {
      key: "pedido",
      header: "Pedido / proposta",
      render: (r) =>
        r.proposta
          ? `${r.proposta.prazo}x de ${fmtBRL(r.proposta.valorParcela)} a ${fmtTaxaFracao(r.proposta.taxaAm)} · troco ${fmtBRL(r.proposta.troco)}`
          : r.pedido
            ? `${r.pedido.parcela != null ? `parcela ${fmtBRL(r.pedido.parcela)}` : `valor ${fmtBRL(r.pedido.valor ?? 0)}`}${r.pedido.prazo ? ` em ${r.pedido.prazo}x` : ""}`
            : "—",
    },
    { key: "sucessor", header: "Sucessor", mono: true, render: (r) => (r.sucessor ? `${r.sucessor.adf} (${r.sucessor.situacao})` : "—") },
    { key: "criadoEm", header: "Criado em", render: (r) => fmtDataHora(r.criadoEm) },
    {
      key: "acoes",
      header: "",
      render: (r) => {
        const a = acoesDoRefin(r, "banco");
        return (
          <div style={{ display: "flex", gap: 6 }}>
            {a.responder ? <Button size="sm" onClick={() => setResponder(r)}>Responder</Button> : null}
            {a.cancelar ? <Button size="sm" variant="ghost" onClick={() => setCancelar(r)}>Cancelar</Button> : null}
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Propostas
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem" }}>Refinanciamentos</h1>
        <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 720 }}>
          Pedidos dos servidores para renegociar contratos com a consignatária, e as ofertas que ela fez pela carteira. Aprovado
          ou aceito, o contrato sucessor entra em Propostas para formalizar e confirmar a averbação; os contratos de origem saem
          da folha quando a ADF dele é aplicada.
        </p>
      </header>

      {aviso ? <div role="status" style={{ color: "var(--emerald-500)", fontWeight: 600 }}>{aviso}</div> : null}

      <FilterBar
        layout="linha"
        searchValue={busca}
        onSearchChange={filtrar(setBusca)}
        searchPlaceholder="Buscar por refinanciamento, servidor, CPF, matrícula ou ADF"
        {...filtrosUi.propsFilterBar}
        actions={
          <FilterChips
            variant="segmentado"
            label="Estado"
            value={estado}
            onChange={(v) => filtrar(setEstado)(v as "" | EstadoRefinanciamento)}
            options={[
              { value: "", label: "Todos", count: semEstado.length },
              ...ESTADOS_FILA.map((e) => ({ value: e, label: ESTADO_REFIN_LABEL_OPERADOR[e], count: contEstado[e] ?? 0 })),
            ]}
          />
        }
      />

      {q.isError ? (
        <div style={{ padding: "12px 16px", borderRadius: 10, border: "1px solid var(--danger-500)", color: "var(--danger-500)", fontSize: 13 }}>
          {mensagemDeErro(q.error, "Não foi possível carregar os refinanciamentos.")}
        </div>
      ) : null}
      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(r) => r.id}
        emptyState={
          <EmptyState
            motivo={motivoVazio(base.length, linhas.length)}
            filtroVazio="Nenhum refinanciamento com esses filtros."
            semDados="Nenhum refinanciamento ainda. Para ofertar, abra um contrato em Meus Contratos e use Refinanciar."
            onLimpar={filtrosUi.limparFiltros}
          />
        }
      />
      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="refinanciamentos"
      />

      {responder ? (
        <ResponderRefinanciamentoModal
          refin={responder}
          onClose={() => setResponder(null)}
          onFeito={(r) =>
            feito(r, r.estado === "aceito" ? "Pedido {id} aprovado: o contrato sucessor está em Propostas." : r.estado === "recusado" ? "Pedido {id} recusado." : "Contraproposta de {id} enviada ao servidor.")
          }
        />
      ) : null}
      {cancelar ? (
        <CancelarRefinanciamentoModal refin={cancelar} onClose={() => setCancelar(null)} onFeito={(r) => feito(r, "Refinanciamento {id} cancelado.")} />
      ) : null}
    </div>
  );
}
